import { useState, createContext, useContext, useEffect } from 'react'
import client from 'socket.io-client'
import { useSelector } from './useSelector'
import firebase from 'firebase/app'
import store from '../store'
import { Game, GameMetadata, GameOptions, Sheet } from '@prometheus/types'
import { setAdminSheets, setConnectionStatusDelta, setGame, setGames, setMetadata, setMetadataDelta, setOptions, setSheet, setSheetDelta, setSheetPatches, setMetadataPatches, setOptionsPatches, setBoardPatches, setPlanetsPatches } from '../reducers/game/game'
import { setConnected } from '../reducers/auth/auth'
import { Patch } from 'immer'

const PORT = process.env.NODE_ENV === 'development' ? ':4000' : ''
// const PATH = `${window.location.protocol}//192.168.0.54${PORT}`
const PATH = `${window.location.protocol}//${window.location.hostname}${PORT}`

interface SetGameResult {
  [key: string]: string
}

const onSetGames = (games: SetGameResult) => {
  if (!games) {
    store.dispatch(setGames([]))
  } else {
    store.dispatch(setGames(Object.values(games).map(s => JSON.parse(s) as Game)))
  }
}

const onSetGame = (game?: Game) => {
  store.dispatch(setGame(game))
}

const onSetSheet = (sheet?: Sheet) => {
  store.dispatch(setSheet(sheet))
}

const onSetConnectionStatusDelta = (delta: any) => {
  store.dispatch(setConnectionStatusDelta(delta))
}

const onSetSheetDelta = (sheetChanges: any) => {
  store.dispatch(setSheetDelta(sheetChanges))
}

const onSetAdminSheets = (sheets?: Sheet[]) => {
  store.dispatch(setAdminSheets(sheets))
}

const onSetMetadata = (metadata?: GameMetadata) => {
  store.dispatch(setMetadata(metadata))
}

const onSetMetadataDelta = (metadataChanges: any) => {
  store.dispatch(setMetadataDelta(metadataChanges))
}

const onSetOptions = (options?: GameOptions) => {
  store.dispatch(setOptions(options))
}

const onSetSheetPatches = (patches: Patch[]) => {
  store.dispatch(setSheetPatches(patches))
}

const onSetMetadataPatches = (patches: Patch[]) => {
  store.dispatch(setMetadataPatches(patches))
}

const onSetOptionsPatches = (patches: Patch[]) => {
  store.dispatch(setOptionsPatches(patches))
}

const onSetBoardPatches = (patches: Patch[]) => {
  store.dispatch(setBoardPatches(patches))
}

const onSetPlanetsPatches = (patches: Patch[]) => {
  store.dispatch(setPlanetsPatches(patches))
}

const onConnect = () => {
  setTimeout(() => {
    store.dispatch(setConnected(true))
  })
}

const onDisconnect = () => {
  setTimeout(() => {
    store.dispatch(setConnected(false))
  })
}

export const SocketContext = createContext<SocketIOClient.Socket | undefined>(undefined)

declare global {
  namespace SocketIOClient {
    interface Socket {
      authenticated?: boolean
    }
  }
}

const authenticatedClient = (authorization: string) => {
  const newClient = client(PATH, {
    transportOptions: {
      polling: {
        extraHeaders: {
          'Authorization': authorization,
        },
      },
    },
  })
  newClient.authenticated = true

  return newClient
}

export const useSocketProvider = () => {
  const uid = useSelector(s => s.auth.uid)
  const guestToken = useSelector(s => s.auth.guestToken)
  const [socket, setSocket] = useState<SocketIOClient.Socket>()

  useEffect(() => {
    if (!socket) {
      return
    }

    socket.on('set-game', onSetGame)
    socket.on('set-games', onSetGames)
    socket.on('set-sheet', onSetSheet)
    socket.on('set-connection-status-delta', onSetConnectionStatusDelta)
    socket.on('set-sheet-delta', onSetSheetDelta)
    socket.on('set-admin-sheets', onSetAdminSheets)
    socket.on('set-metadata', onSetMetadata)
    socket.on('set-metadata-delta', onSetMetadataDelta)
    socket.on('set-options', onSetOptions)
    socket.on('connect', onConnect)
    socket.on('disconnect', onDisconnect)
    
    // New patch-based handlers
    socket.on('set-sheet-patches', onSetSheetPatches)
    socket.on('set-metadata-patches', onSetMetadataPatches)
    socket.on('set-options-patches', onSetOptionsPatches)
    socket.on('set-board-patches', onSetBoardPatches)
    socket.on('set-planets-patches', onSetPlanetsPatches)
  }, [socket])

  useEffect(() => {
    // Guests authenticate with their play key, signed-in players with a
    // Firebase ID token; both land on `socket.profile.uid` server side, so
    // nothing downstream has to care which one it was. With neither, the
    // socket still connects — it can watch the lobby and mint a guest.
    if (guestToken) {
      setSocket(authenticatedClient(`Guest ${guestToken}`))
    } else if (uid) {
      firebase.auth().currentUser?.getIdToken(true).then((token) => {
        setSocket(authenticatedClient(`Bearer ${token}`))
      })
    } else {
      setSocket(client(PATH))
    }

    return () => {
      setSocket(s => {
        if (s) {
          s.close()
        }

        return undefined
      })
    }
  }, [uid, guestToken])

  return socket
}

export const useSocket = () => {
  const socket = useContext(SocketContext)

  return socket
}
